import type { MusicData } from '@/types/music';
import { createClient } from '@supabase/supabase-js';

// 환경 변수 가져오기 및 상세 디버깅
const getEnvVar = (key: string): string => {
  const value = import.meta.env[key] || '';
  if (!value && import.meta.env.DEV) {
    console.warn(`Environment variable ${key} is not set or empty`);
  }
  return value;
};

// 환경 변수 상태 확인 함수 추가
export function checkEnvironmentVariables() {
  const requiredVars = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'];

  const optionalVars = [
    'VITE_DB_HOST',
    'VITE_DB_PORT',
    'VITE_DB_USER',
    'VITE_DB_PASSWORD',
    'VITE_DB_NAME',
  ];

  const issues: string[] = [];
  const status: Record<string, boolean> = {};

  // 필수 환경 변수 확인
  for (const varName of requiredVars) {
    const value = import.meta.env[varName] || '';
    status[varName] = !!value;

    if (!value) {
      issues.push(`필수 환경 변수 ${varName}이(가) 설정되지 않았습니다.`);
    }
  }

  // 선택 환경 변수 확인
  for (const varName of optionalVars) {
    const value = import.meta.env[varName] || '';
    status[varName] = !!value;
  }

  const allRequiredSet = requiredVars.every((varName) => status[varName]);

  return {
    success: allRequiredSet,
    issues,
    status,
    message: allRequiredSet
      ? '모든 필수 환경 변수가 설정되었습니다.'
      : `${issues.length}개의 환경 변수 문제가 발견되었습니다.`,
  };
}

// music_data 전체 개수 조회 함수
export async function getMusicDataCount(date?: string) {
  try {
    let query = supabase.from('music_data').select('count', { count: 'exact' });

    // 날짜 필터링 적용
    if (date) {
      query = query.eq('broadcast_date', date);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Count fetch error:', error);
      return {
        success: false,
        message: error.message,
        error,
      };
    }

    return {
      success: true,
      count: data?.[0]?.count || 0,
    };
  } catch (error) {
    console.error('Unexpected error while fetching count:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error',
      error,
    };
  }
}

const supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');

// 개발 환경에서 연결 정보 로깅 (향상된 디버깅)
if (import.meta.env.DEV) {
  console.log('Environment variables check:');
  console.log('- VITE_SUPABASE_URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
  console.log(
    '- VITE_SUPABASE_ANON_KEY:',
    supabaseAnonKey ? `✅ Set (starts with: ${supabaseAnonKey.substring(0, 10)}...)` : '❌ Missing'
  );

  // 모든 VITE_ 환경 변수 확인
  console.log('All VITE_ environment variables:');
  for (const key of Object.keys(import.meta.env)) {
    if (key.startsWith('VITE_')) {
      const value = import.meta.env[key];
      const isSensitive = key.includes('KEY') || key.includes('PASSWORD');
      const displayValue =
        isSensitive && value
          ? `***${value.toString().substring(value.toString().length - 4)}`
          : value
            ? '✅ Set'
            : '❌ Empty';
      console.log(`- ${key}: ${displayValue}`);
    }
  }
}

// 연결 정보가 없을 경우 명확한 오류 출력
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('🚨 Supabase credentials are missing!');
  console.error(`- URL: ${supabaseUrl ? 'Set ✅' : 'Missing ❌'}`);
  console.error(`- API Key: ${supabaseAnonKey ? 'Set ✅' : 'Missing ❌'}`);
  console.error(
    'Please check your .env file and make sure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are defined correctly'
  );
}

// 디버그용 옵션
const debugOptions = import.meta.env.DEV
  ? {
      logger: {
        debug: console.info,
        info: console.info,
        warn: console.warn,
        error: console.error,
      },
    }
  : {};

// Supabase 클라이언트 생성 (향상된 옵션) - PGRST100 오류 수정
console.log('Creating Supabase client with proper configuration...');

// URL에 apikey 파라미터를 자동으로 추가하는 커스텀 fetch 함수
const customFetch = (url: URL | RequestInfo, options?: RequestInit) => {
  let finalUrl = url;

  // URL 인스턴스가 아닌 경우 변환
  if (!(url instanceof URL) && typeof url === 'string') {
    try {
      finalUrl = new URL(url);
      // URL 파라미터에 apikey 추가
      if (finalUrl.origin.includes('supabase')) {
        (finalUrl as URL).searchParams.append('apikey', supabaseAnonKey);
      }
    } catch (e) {
      // 유효한 URL이 아닌 경우 원래 URL 유지
      finalUrl = url;
    }
  }

  // 디버깅용 로그
  if (import.meta.env.DEV) {
    console.log(
      `Fetch request to: ${typeof finalUrl === 'string' ? finalUrl.split('?')[0] : 'URL object'}`
    );
  }

  // 헤더에 apikey 추가 보장
  const finalOptions = options || {};
  finalOptions.headers = {
    ...finalOptions.headers,
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${supabaseAnonKey}`,
  };

  return fetch(finalUrl, finalOptions);
};

const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
      'X-Client-Info': 'supabase-js/2.x',
    },
    fetch: customFetch,
  },
  ...debugOptions,
});

// 명시적 API 키 확인 로깅
console.log(
  `Supabase client initialized with URL: ${
    supabaseUrl ? `${supabaseUrl.substring(0, 20)}...` : 'missing'
  }`
);
console.log(
  `API key correctly set: ${supabaseAnonKey ? '✅' : '❌'} (length: ${supabaseAnonKey.length})`
);

export { supabase };

// 직접 Supabase API 연결 테스트 (API 키 문제 진단용)
export async function testDirectConnection() {
  try {
    // URL 파라미터에 apikey 추가
    const url = new URL(`${supabaseUrl}/rest/v1/music_data`);
    url.searchParams.append('apikey', supabaseAnonKey);
    url.searchParams.append('select', '*');
    url.searchParams.append('limit', '1');

    // 디버그를 위한 URL 로깅 (민감 정보 일부 가림)
    const debugUrl = new URL(url.toString());
    const shortKey = `${supabaseAnonKey.substring(0, 10)}...`;
    debugUrl.searchParams.set('apikey', shortKey);
    console.log(`API Test - URL: ${debugUrl.toString()}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Prefer: 'count=exact',
      },
    });

    const status = response.status;
    let responseData: unknown;

    try {
      responseData = await response.json();
    } catch (e) {
      responseData = await response.text();
    }

    // 디버그 정보 추가
    console.log(`API Test - Status: ${status}`);
    console.log('API Key length:', supabaseAnonKey.length);
    console.log('Headers sent:', {
      apikey: supabaseAnonKey ? `${supabaseAnonKey.substring(0, 10)}...` : 'missing',
      Authorization: supabaseAnonKey ? 'Bearer (set)' : 'missing',
    });
    console.log('Response data:', responseData);

    // 테이블 존재 여부 확인
    const { data: tableInfo, error: tableError } = await supabase
      .from('music_data')
      .select('*')
      .limit(1);

    console.log('Table check result:', {
      hasData: !!tableInfo,
      dataCount: tableInfo ? tableInfo.length : 0,
      error: tableError,
    });

    return {
      success: response.ok,
      status,
      message: response.ok
        ? 'Direct connection successful'
        : `Connection failed with status ${status}`,
      responseData,
      tableExists: !!tableInfo,
      hasData: tableInfo ? tableInfo.length > 0 : false,
      tableError,
    };
  } catch (error) {
    console.error('Direct API connection test failed:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown connection error',
      error,
    };
  }
}

// 연결 상태 확인 함수
export async function checkSupabaseConnection() {
  try {
    console.log('Testing Supabase connection...');

    // 직접 연결 테스트 먼저 실행
    const directTest = await testDirectConnection();
    console.log('Direct API test result:', directTest);

    if (!directTest.success) {
      return {
        success: false,
        message: `Direct API test failed: ${directTest.message}`,
        details: directTest,
      };
    }

    // Supabase 클라이언트로 테스트
    const { count, error } = await supabase
      .from('music_data')
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.error('Supabase client error:', error);
      throw new Error(`Supabase connection error: ${error.message}`);
    }

    return {
      success: true,
      message: 'Connection successful',
      count: count || 0,
    };
  } catch (error) {
    console.error('Supabase connection check failed:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown connection error',
      error,
    };
  }
}

// 테이블 구조 및 데이터 확인
export async function diagnoseMusicTable() {
  try {
    console.log('Diagnosing music_data table...');

    // 데이터 수 확인 (getMusicDataCount 사용)
    const countResult = await getMusicDataCount();
    if (!countResult.success) {
      return {
        success: false,
        exists: true,
        message: `데이터 수 확인 오류: ${countResult.message}`,
        error: countResult.error,
      };
    }

    // 샘플 데이터 조회
    const { data: sampleData, error: sampleError } = await supabase
      .from('music_data')
      .select('*')
      .limit(3);

    return {
      success: !sampleError,
      exists: true,
      message: sampleError ? `샘플 데이터 조회 오류: ${sampleError.message}` : '진단 성공',
      sampleData,
      count: countResult.count || 0,
    };
  } catch (error) {
    console.error('Diagnosis error:', error);
    return {
      success: false,
      exists: false,
      message: error instanceof Error ? error.message : '알 수 없는 진단 오류',
      error,
    };
  }
}

// 샘플 데이터 삽입 (테이블이 비어있을 때 사용)
export async function insertSampleData() {
  try {
    console.log('Attempting to insert sample data...');

    // 먼저 테이블 존재 여부 확인
    const diagnosis = await diagnoseMusicTable();

    // 테이블이 존재하지 않으면 생성 시도
    if (!diagnosis.exists) {
      console.log('Table does not exist, creating it...');
      const result = await createMusicTable();
      if (!result.success) {
        return {
          success: false,
          message: `테이블 생성 실패: ${result.message}`,
          error: result.error,
        };
      }
    }

    const sampleData = [
      {
        broadcast_date: '2023-01-01',
        number: 1,
        title: 'Sample Song 1',
        artist: 'Sample Artist 1',
      },
      {
        broadcast_date: '2023-01-01',
        number: 2,
        title: 'Sample Song 2',
        artist: 'Sample Artist 2',
      },
      {
        broadcast_date: '2023-01-01',
        number: 3,
        title: 'Sample Song 3',
        artist: 'Sample Artist 3',
      },
      {
        broadcast_date: '2023-01-08',
        number: 1,
        title: 'Sample Song 4',
        artist: 'Sample Artist 4',
      },
      {
        broadcast_date: '2023-01-08',
        number: 2,
        title: 'Sample Song 5',
        artist: 'Sample Artist 5',
      },
    ];

    console.log('Inserting sample data:', sampleData.length, 'records');

    const { data, error } = await supabase.from('music_data').insert(sampleData).select();

    if (error) {
      console.error('Sample data insertion error:', error);
      return {
        success: false,
        message: `삽입 오류: ${error.message}`,
        error,
      };
    }

    console.log('Sample data inserted successfully:', data?.length || 0, 'records');

    return {
      success: true,
      message: '샘플 데이터가 성공적으로 추가되었습니다',
      data,
    };
  } catch (error) {
    console.error('Sample data insertion error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '알 수 없는 삽입 오류',
      error,
    };
  }
}

// Supabase REST API를 통해 테이블 생성
export async function createMusicTable() {
  try {
    console.log('Attempting to create music_data table...');

    // SQL 쿼리 직접 실행은 권한이 필요하므로 RPC 함수를 통해 처리해야 함
    // 여기서는 간단한 알림으로 대체 (실제 구현은 Supabase 대시보드 사용 권장)
    console.warn('Table creation should be done via Supabase dashboard');

    return {
      success: false,
      message:
        'API를 통한 테이블 생성은 지원되지 않습니다. Supabase 대시보드에서 테이블을 생성해주세요.',
      instructions:
        '1. Supabase 대시보드 > 테이블 > 새 테이블 생성\n' +
        '2. 이름: music_data\n' +
        '3. 스키마: public\n' +
        '4. 컬럼: seqid(bigint), broadcast_date(date), number(int), title(text), artist(text)\n' +
        '5. seqid를 기본 키로 설정',
    };
  } catch (error) {
    console.error('Table creation error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '알 수 없는 테이블 생성 오류',
      error,
    };
  }
}

// 직접 SQL 쿼리 실행 (고급 진단용)
export async function executeSqlQuery(sqlQuery: string) {
  try {
    // 참고: Supabase의 기본 플랜에서는 RPC 함수 사용이 제한될 수 있음
    // 대신 REST API를 통해 직접 쿼리를 실행하는 방식은 권장되지 않음

    // 대체 방법으로, 쿼리 종류에 따라 적절한 API 호출을 사용
    const isSelect = sqlQuery.trim().toLowerCase().startsWith('select');

    if (isSelect) {
      // SELECT 쿼리는 간접적으로 처리할 수 있음
      console.warn('Direct SQL execution is limited in Supabase. Using API instead.');

      return {
        success: false,
        message: 'Direct SQL execution is not available. Please use Supabase API instead.',
        data: null,
      };
    }
    return {
      success: false,
      message: 'Non-SELECT SQL execution is not supported in this client interface.',
      data: null,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown query error',
      error,
    };
  }
}

// 문제해결을 위한 기본 테이블 생성
export async function createMusicDataTable() {
  try {
    // 참고: Supabase의 기본 플랜에서는 직접 CREATE TABLE 실행이 제한됨
    // 테이블은 Supabase 대시보드에서 생성하는 것이 권장됨

    console.warn('Table creation via client is not supported. Please use Supabase dashboard.');

    // 대신 테이블이 있는지 확인
    const { data, error } = await supabase
      .from('music_data')
      .select('count(*)', { count: 'exact', head: true });

    if (error) {
      const errorObj = error as { message?: string };
      const errorMessage = errorObj?.message || String(error);
      return {
        success: false,
        message: `Table check failed: ${errorMessage}. Please create the table via Supabase dashboard.`,
        error,
      };
    }

    return {
      success: true,
      message: 'Music data table exists and is accessible',
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown table creation error',
      error,
    };
  }
}

// 캐시 설정 인터페이스
interface CacheConfig {
  maxSize: number;
  ttl: number;
  namespace?: string;
}

// 캐시 엔트리 인터페이스
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  key: string;
}

// 개선된 캐시 클래스
class EnhancedCache {
  private cache: Map<string, CacheEntry<unknown>>;
  private readonly config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      maxSize: config.maxSize || 100,
      ttl: config.ttl || 5 * 60 * 1000, // 기본 5분
      namespace: config.namespace || 'default',
    };
    this.cache = new Map();
  }

  private createKey(key: string): string {
    return `${this.config.namespace}:${key}`;
  }

  private isExpired(entry: CacheEntry<unknown>): boolean {
    return Date.now() - entry.timestamp > this.config.ttl;
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
      }
    }
  }

  get<T>(key: string): T | null {
    const fullKey = this.createKey(key);
    const entry = this.cache.get(fullKey);

    if (!entry) return null;

    if (this.isExpired(entry)) {
      this.cache.delete(fullKey);
      return null;
    }

    if (import.meta.env.DEV) {
      console.debug(`Cache hit for key: ${key}`);
    }

    return entry.data as T;
  }

  set<T>(key: string, data: T): void {
    const fullKey = this.createKey(key);

    // 캐시 크기 제한 체크
    if (this.cache.size >= this.config.maxSize) {
      // LRU 방식으로 가장 오래된 항목 제거
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(fullKey, {
      data,
      timestamp: Date.now(),
      key: fullKey,
    });

    if (import.meta.env.DEV) {
      console.debug(`Cache set for key: ${key}`);
    }
  }

  // 특정 키 패턴에 대한 캐시 무효화
  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    for (const [key] of this.cache.entries()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  // 특정 네임스페이스의 캐시만 삭제
  clearNamespace(namespace: string): void {
    const prefix = `${namespace}:`;
    for (const [key] of this.cache.entries()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  // 전체 캐시 삭제
  clear(): void {
    this.cache.clear();
  }

  // 캐시 상태 정보 반환
  getStats(): { size: number; maxSize: number; ttl: number } {
    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      ttl: this.config.ttl,
    };
  }
}

// 전역 캐시 인스턴스 생성 (설정 가능)
const cache = new EnhancedCache({
  maxSize: 1000,
  ttl: 5 * 60 * 1000, // 5분
  namespace: 'music-data',
});

// 직접 API를 통해 데이터 가져오기
export async function fetchMusicDataDirect(
  page = 0,
  pageSize = 10,
  date?: string
): Promise<{
  success: boolean;
  data: MusicData[];
  totalCount: number;
  message: string;
  error?: unknown;
}> {
  try {
    const tableName = 'music_data';

    // Get count using getMusicDataCount with date filter
    const countResult = await getMusicDataCount(date);
    if (!countResult.success) {
      throw new Error(`Count fetch failed: ${countResult.message}`);
    }

    // Create base query
    let query = supabase
      .from(tableName)
      .select('*')
      .order('broadcast_date', { ascending: false })
      .order('number', { ascending: true });

    // Add date filter if provided
    if (date) {
      query = query.eq('broadcast_date', date);
    }

    // Add pagination
    const { data, error } = await query.range(page * pageSize, (page + 1) * pageSize - 1);

    if (error) {
      console.error('Data fetch failed:', error);
      throw new Error(`데이터 조회 오류: ${error.message}`);
    }

    return {
      success: true,
      data: data || [],
      totalCount: countResult.count || 0,
      message: 'Data fetched successfully',
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      totalCount: 0,
      message: error instanceof Error ? error.message : 'Unknown fetch error',
      error,
    };
  }
}

// Fetch the most recent date with data in the music_data table
export async function fetchLatestDate(): Promise<{
  success: boolean;
  latestDate: string | null;
  message: string;
  error?: unknown;
}> {
  try {
    const { data, error } = await supabase
      .from('music_data')
      .select('broadcast_date')
      .order('broadcast_date', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Latest date fetch failed:', error);
      throw new Error(`최신 날짜 조회 오류: ${error.message}`);
    }

    const latestDate = data && data.length > 0 ? data[0].broadcast_date : null;

    return {
      success: true,
      latestDate,
      message: latestDate ? `Latest date found: ${latestDate}` : 'No data found in the table',
    };
  } catch (error) {
    return {
      success: false,
      latestDate: null,
      message: error instanceof Error ? error.message : 'Unknown fetch error',
      error,
    };
  }
}
